import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import PortugalSpainQuickFitCheck from "@/components/PortugalSpainQuickFitCheck";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ_ITEMS = [
  {
    question: "Is Portugal usually cheaper than Spain for Americans moving abroad?",
    answer:
      "Portugal often looks more predictable on cost, especially outside Lisbon. Spain can still be workable, but the budget picture usually varies more by region, city choice, and daily expectations.",
  },
  {
    question: "Does Spain usually have stronger healthcare than Portugal?",
    answer:
      "Spain often feels stronger if public-system depth and healthcare confidence are major priorities. Portugal can still work very well, especially for people comfortable using a public-private mix for faster access.",
  },
  {
    question: "Is Portugal easier than Spain when residency practicality matters most?",
    answer:
      "Portugal often feels more approachable for Americans who want an easier transition or a softer administrative landing. Spain remains viable, but the process can feel more structured depending on the pathway and region.",
  },
  {
    question: "Should one strong factor decide the whole comparison?",
    answer:
      "Usually no. A country can look stronger on healthcare, cost, or lifestyle alone and still be the weaker overall fit once the rest of the move is considered together. That is why Relocation Clarity emphasizes tradeoffs rather than one-factor rankings.",
  },
];

export default function PortugalVsSpainLivingAbroad() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="portugal-vs-spain-living-abroad-page">
      <SeoArticleSchema
        headline="Portugal vs Spain: Which Country Fits Your Priorities?"
        description="Compare Portugal and Spain across cost, healthcare, residency practicality, and lifestyle tradeoffs so you can see which one fits your priorities more clearly."
        pathname="/insights/portugal-vs-spain-living-abroad"
        keywords={[
          "Portugal vs Spain living abroad",
          "Portugal vs Spain expat comparison",
          "which country fits your priorities Portugal or Spain",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="living-abroad-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="living-abroad-hero-accent" />
            <p className="rc-section-kicker mb-4" data-testid="living-abroad-hero-kicker">Decision guide</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="living-abroad-page-title">
              Portugal vs Spain: Which Country Fits Your Priorities?
            </h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-page-intro">
              <p>Portugal and Spain are two of the most popular countries for Americans moving abroad.</p>
              <p>Both offer strong healthcare, good infrastructure, and a high quality of life—but they fit different priorities.</p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="living-abroad-key-takeaway"
            paragraphs={[
              "Portugal and Spain both stay near the top of many living-abroad shortlists because each offers strong quality of life, workable healthcare options, and established appeal for Americans.",
              "Portugal often pulls ahead on lower cost and easier day-to-day setup. Spain often pulls ahead on healthcare strength, infrastructure, and regional variety. The better fit depends on which tradeoff matters more once daily life becomes the focus.",
            ]}
          />

          <PortugalSpainQuickFitCheck quickFitType="portugal_spain_general" />

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="living-abroad-faq-section">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="living-abroad-faq-kicker">Questions people usually still have</p>
              <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="living-abroad-faq-title">Still Wondering About Portugal vs. Spain?</h2>
              <p className="mt-4 text-base leading-8 text-stone-600" data-testid="living-abroad-faq-description">
                These are the practical questions that often come up after the Quick Fit™ points you in one direction but before you feel ready to make the larger decision.
              </p>
            </div>

            <Accordion type="single" collapsible className="mt-7 space-y-3" data-testid="living-abroad-faq-accordion">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`faq-${index + 1}`}
                  className="rounded-[1.4rem] border border-stone-200 bg-stone-50/75 px-5"
                  data-testid={`living-abroad-faq-item-${index + 1}`}
                >
                  <AccordionTrigger
                    className="py-5 text-left font-manrope text-lg text-stone-900 no-underline hover:no-underline"
                    data-testid={`living-abroad-faq-trigger-${index + 1}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="pb-5 text-sm leading-7 text-stone-600" data-testid={`living-abroad-faq-answer-${index + 1}`}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="living-abroad-quick-answer-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-quick-answer-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-quick-answer-title">What is the quick comparison?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-quick-answer-copy">
                <p>Choose Portugal if you want lower cost, simpler setup, and a more consistent day-to-day experience.</p>
                <p>Choose Spain if you value stronger public healthcare and more regional variety in lifestyle.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-cost-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-cost-title">How does cost change the comparison?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-cost-copy">
                <p>Portugal is generally more affordable, especially outside Lisbon.</p>
                <p>Spain varies more. Madrid and Barcelona are expensive, while smaller cities are more manageable.</p>
                <p className="text-stone-800">Portugal is the more predictable option if budget matters most.</p>
              </div>
            </article>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="living-abroad-healthcare-residency-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-healthcare-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-healthcare-title">How important is healthcare compared to day-to-day access?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-healthcare-copy">
                <p>Portugal offers strong access through a mix of public and private care, with many expats using private clinics for faster service.</p>
                <p>Spain has one of the strongest public healthcare systems in Europe, but access and wait times vary by region.</p>
                <p className="text-stone-800">Spain is stronger on system quality. Portugal is often easier day to day.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-residency-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-residency-title">Which country is usually easier to pursue in practice?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-residency-copy">
                <p>Portugal is known for more flexible residency pathways for Americans, especially for retirement or remote income.</p>
                <p>Spain also offers viable options, but requirements can feel more structured and location-dependent.</p>
                <p className="text-stone-800">Portugal is generally easier to navigate.</p>
              </div>
            </article>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="living-abroad-lifestyle-tradeoff-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-lifestyle-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-lifestyle-title">How do lifestyle and pace differ?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-lifestyle-copy">
                <p>Portugal tends to be slower and more relaxed across most regions.</p>
                <p>Spain offers more variety—some areas are fast-paced, others slower.</p>
                <p className="text-stone-800">Portugal is more consistent. Spain offers more range.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="living-abroad-key-tradeoff-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-key-tradeoff-title">What is the key tradeoff between Portugal and Spain?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-key-tradeoff-copy">
                <p>Portugal is easier and more predictable.</p>
                <p>Spain is stronger in certain areas but requires more careful selection of location and setup.</p>
              </div>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="living-abroad-fit-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-fit-title">Which one is more likely to fit you?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-fit-copy">
              <p>There’s no single better choice.</p>
              <p>The right decision depends on how your priorities—cost, healthcare, residency, and daily pace—line up with each country.</p>
            </div>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="living-abroad-decision-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="living-abroad-decision-title">What should you do once both countries still look viable?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="living-abroad-decision-copy">
              <p>Most people don’t struggle comparing countries.</p>
              <p>They struggle deciding which one fits their life.</p>
              <p>Relocation Clarity helps you compare countries based on your priorities so you can see what actually works before you commit.</p>
              <p>
                If you are not down to two clear options yet, start by building{" "}
                <Link
                  to="/insights/create-country-shortlist-for-moving-abroad"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="living-abroad-shortlist-link"
                >
                  a realistic shortlist
                </Link>
                .
              </p>
              <p data-testid="living-abroad-early-process-note">
                If you are still early in the process, start by narrowing your broader country shortlist first.
              </p>
              <p>
                If you are looking specifically through a retirement lens, you may also want to compare this article with{" "}
                <Link to="/insights/portugal-vs-spain-for-american-retirees" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="living-abroad-retirement-comparison-link">
                  Portugal vs Spain for American retirees
                </Link>
                , which gives the same pair a more retirement-specific framing.
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Continue exploring"
            title="If you are not ready for the full assessment yet, these are the most useful next pages from here."
            description="You can keep learning without losing the thread: preview how a fuller recommendation is organized, or go one level deeper on Portugal or Spain before you personalize the comparison." 
            testIdPrefix="living-abroad-next-steps"
            items={[
              {
                slug: "sample-blueprint",
                eyebrow: "See the full structure",
                title: "Preview a Sample Blueprint",
                description: "See how a comparison like this turns into a fuller shortlist, tradeoff review, and decision path.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
              {
                slug: "portugal-profile",
                eyebrow: "Go deeper on Portugal",
                title: "Explore the Portugal country profile",
                description: "Look at Portugal in more detail if it still feels like a live option after the comparison.",
                ctaLabel: "See Portugal",
                to: "/country/portugal",
              },
              {
                slug: "spain-profile",
                eyebrow: "Go deeper on Spain",
                title: "Explore the Spain country profile",
                description: "Look at Spain in more detail if you want more context before turning the decision into a personalized shortlist.",
                ctaLabel: "See Spain",
                to: "/country/spain",
              },
            ]}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}